import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  UploadCloud, FileUp, Sun, Moon, Loader2, Ruler,
  Percent, Layers, Hash, Tag, RotateCcw, Download, FileDown, Maximize,
  ChevronDown, Grid3x3, FileArchive, LogOut, Building2, Users, Printer,
  ClipboardList, ListChecks,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import DxfCanvas from "@/components/DxfCanvas";
import api, { errorMessage } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { LOGOUT } from "@/constants/testIds";

const fmt = (n, dp = 2) => Number(n).toFixed(dp).replace(".", ",");
const fmtM = (n) => `${Number(n).toFixed(3).replace(".", ",")} m`;
const fmtCm = (n) => `${(Number(n) * 100).toFixed(1).replace(".", ",")} cm`;

// bbox dimensions of a piece boundary (coords in meters)
function pieceDims(piece) {
  const b = piece?.boundary || [];
  if (b.length < 2) return null;
  let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
  for (const [x, y] of b) {
    if (x < minx) minx = x;
    if (x > maxx) maxx = x;
    if (y < miny) miny = y;
    if (y > maxy) maxy = y;
  }
  return { w: maxx - minx, h: maxy - miny, area: piece.area_m2 };
}

function useTheme() {
  const [dark, setDark] = useState(() =>
    document.documentElement.classList.contains("dark")
  );
  const toggle = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
  };
  return { dark, toggle };
}

function MetaCell({ icon: Icon, label, value, testid, accent }) {
  return (
    <div className="p-3 border-r border-b border-border last:border-r-0">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div
        data-testid={testid}
        className={`mono text-lg font-medium leading-none ${accent ? "text-[hsl(var(--success))]" : "text-foreground"}`}
      >
        {value}
      </div>
    </div>
  );
}

export default function Viewer() {
  const { dark, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [hoveredUid, setHoveredUid] = useState(null);
  const [showLabels, setShowLabels] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportLayers, setReportLayers] = useState("");
  const [generatingReport, setGeneratingReport] = useState(false);
  const inputRef = useRef(null);
  const highlightKey = hoveredUid;
  const setHighlightKey = setHoveredUid;

  const exportDxf = useCallback(async (pieces, filename) => {
    if (!pieces || pieces.length === 0) return;
    setExporting(true);
    try {
      const res = await api.post(
        "/export-dxf",
        {
          // Only the exterior (cut) line — internals (net/sewing line) are dropped
          pieces: pieces.map((p) => ({ name: p.name, size: p.size, boundary: p.boundary, internals: [], centroid: p.centroid })),
          filename,
          units: "mm",
        },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename.endsWith(".dxf") ? filename : `${filename}.dxf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("DXF exportado", { description: `${pieces.length} molde(s) · só linha exterior · mm` });
    } catch (e) {
      toast.error("Erro ao exportar DXF", { description: "Tente novamente." });
    } finally {
      setExporting(false);
    }
  }, []);

  const exportZip = useCallback(async (pieces, filename) => {
    if (!pieces || pieces.length === 0) return;
    setExporting(true);
    try {
      const res = await api.post(
        "/export-dxf-zip",
        {
          pieces: pieces.map((p) => ({ name: p.name, size: p.size, boundary: p.boundary, internals: [], centroid: p.centroid })),
          filename,
        },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename.endsWith(".zip") ? filename : `${filename}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("ZIP exportado", { description: "1 DXF por tamanho · só linha exterior · mm" });
    } catch (e) {
      toast.error("Erro ao exportar ZIP", { description: "Tente novamente." });
    } finally {
      setExporting(false);
    }
  }, []);

  const upload = useCallback(async (file) => {
    if (!file) return;
    const lower = file.name.toLowerCase();
    const isDxf = lower.endsWith(".dxf");
    const isGbr = lower.endsWith(".gbr");
    if (!isDxf && !isGbr) {
      toast.error("Formato inválido", { description: "Selecione um ficheiro .dxf ou .gbr." });
      return;
    }
    setLoading(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await api.post(`/${isDxf ? "parse-dxf" : "parse-gbr"}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setData(res.data);
      setHoveredUid(null);
      toast.success("Ficheiro carregado", {
        description: `${res.data.piece_count} peças · ${res.data.filename}`,
      });
    } catch (e) {
      const msg = errorMessage(e, "Não foi possível processar o ficheiro.");
      toast.error(`Erro ao ler ${isDxf ? "DXF" : "GBR"}`, { description: msg });
    } finally {
      setLoading(false);
    }
  }, []);

  const exportGrid = useCallback(async (pieces, filename) => {
    if (!pieces || pieces.length === 0) return;
    setExporting(true);
    try {
      const res = await api.post(
        "/export-dxf-grid",
        {
          pieces: pieces.map((p) => ({ name: p.name, size: p.size, boundary: p.boundary, internals: [], centroid: p.centroid })),
          filename,
        },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename.endsWith(".dxf") ? filename : `${filename}.dxf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("DXF em grelha exportado", { description: "Linhas = peças · colunas = tamanhos · mm" });
    } catch (e) {
      toast.error("Erro ao exportar grelha", { description: "Tente novamente." });
    } finally {
      setExporting(false);
    }
  }, []);

  const generateReport = useCallback(async (e) => {
    e.preventDefault();
    const layers = Number(reportLayers);
    if (!Number.isInteger(layers) || layers < 1) {
      toast.error("Nº de camadas inválido", { description: "Indica um número inteiro de 1 ou mais." });
      return;
    }
    setGeneratingReport(true);
    try {
      const res = await api.post(
        "/report-pdf",
        { ...data, layers },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${data.id}_relatorio.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Relatório PDF gerado", { description: `${layers} camadas` });
      setReportOpen(false);
    } catch (err) {
      toast.error("Erro ao gerar relatório", { description: errorMessage(err) });
    } finally {
      setGeneratingReport(false);
    }
  }, [data, reportLayers]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    upload(file);
  };

  const selectedPiece = data && highlightKey
    ? data.pieces.find((p) => `${p.name}||${p.size || "-"}` === highlightKey)
    : null;
  const dims = selectedPiece ? pieceDims(selectedPiece) : null;

  const loadSample = () => {
    setLoading(true);
    fetch(`${process.env.PUBLIC_URL || ""}/exemplo_modaris.dxf`)
      .then((r) => r.blob())
      .then((b) => upload(new File([b], "exemplo_modaris.dxf")))
      .catch(() => { setLoading(false); toast.error("Exemplo indisponível"); });
  };

  const loadSampleGbr = () => {
    setLoading(true);
    fetch(`${process.env.PUBLIC_URL || ""}/0307261156.gbr`)
      .then((r) => r.blob())
      .then((b) => upload(new File([b], "0307261156.gbr")))
      .catch(() => { setLoading(false); toast.error("Exemplo indisponível"); });
  };

  return (
    <div className="h-screen flex flex-col bg-background text-foreground overflow-hidden">
      {/* Header */}
      <header className="h-14 shrink-0 border-b border-border flex items-center justify-between px-4 bg-card">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-foreground flex items-center justify-center">
            <Layers className="w-4 h-4 text-background" />
          </div>
          <div className="leading-none">
            <h1 className="font-head text-sm font-bold tracking-tight">LOMARTEX · MOLDES EXPORTER v1.0</h1>
            <p className="text-[10px] text-muted-foreground mono mt-0.5">DXF / GBR · Planos de corte</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <div className="hidden sm:flex items-center gap-2 mr-2 px-2.5 h-9 border border-border">
              <Tag className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs mono text-muted-foreground">Etiquetas</span>
              <Switch data-testid="labels-toggle" checked={showLabels} onCheckedChange={setShowLabels} />
            </div>
          )}
          <button
            data-testid="theme-toggle"
            onClick={toggle}
            className="w-9 h-9 border border-border flex items-center justify-center hover:bg-accent transition-colors"
            title="Alternar tema"
          >
            {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <button
            data-testid="orders-link"
            onClick={() => navigate("/ordens")}
            title="Ordens de corte"
            className="h-9 px-3 flex items-center gap-2 border border-border text-sm font-medium rounded-[4px] hover:bg-accent transition-colors"
          >
            <ClipboardList className="w-4 h-4" /> <span className="hidden sm:inline">Ordens</span>
          </button>
          <button
            data-testid="production-link"
            onClick={() => navigate("/producao")}
            title="Painel de produção"
            className="h-9 px-3 flex items-center gap-2 border border-border text-sm font-medium rounded-[4px] hover:bg-accent transition-colors"
          >
            <ListChecks className="w-4 h-4" /> <span className="hidden sm:inline">Produção</span>
          </button>
          {data && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="export-menu-btn"
                  disabled={exporting}
                  className="h-9 px-3 flex items-center gap-2 border border-border text-sm font-medium rounded-[4px] hover:bg-accent transition-colors disabled:opacity-50"
                  title="Opções de exportação"
                >
                  {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} Exportar
                  <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72 rounded-[4px]">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">Exportar (só linha exterior · mm)</DropdownMenuLabel>
                <DropdownMenuItem data-testid="export-grid-item" className="rounded-none cursor-pointer" onClick={() => exportGrid(data.pieces, `${data.id}_grelha`)}>
                  <Grid3x3 className="w-4 h-4 mr-2" />
                  <div className="flex flex-col">
                    <span className="text-sm">DXF em grelha</span>
                    <span className="text-[11px] text-muted-foreground">Um ficheiro · linhas = peças, colunas = tamanhos</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="export-zip-item" className="rounded-none cursor-pointer" onClick={() => exportZip(data.pieces, `${data.id}_moldes`)}>
                  <FileArchive className="w-4 h-4 mr-2" />
                  <div className="flex flex-col">
                    <span className="text-sm">ZIP (1 por tamanho)</span>
                    <span className="text-[11px] text-muted-foreground">Um ficheiro DXF por cada molde/tamanho</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="export-single-item" className="rounded-none cursor-pointer" onClick={() => exportDxf(data.pieces, `${data.id}_todas`)}>
                  <FileDown className="w-4 h-4 mr-2" />
                  <div className="flex flex-col">
                    <span className="text-sm">DXF único (posições originais)</span>
                    <span className="text-[11px] text-muted-foreground">Como no ficheiro de origem (pode sobrepor)</span>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {data && (
            <Dialog open={reportOpen} onOpenChange={setReportOpen}>
              <DialogTrigger asChild>
                <button
                  data-testid="report-pdf-btn"
                  className="h-9 px-3 flex items-center gap-2 border border-border text-sm font-medium rounded-[4px] hover:bg-accent transition-colors"
                  title="Gerar relatório PDF"
                >
                  <Printer className="w-4 h-4" /> Relatório
                </button>
              </DialogTrigger>
              <DialogContent className="max-w-sm">
                <DialogHeader>
                  <DialogTitle>Relatório PDF do plano</DialogTitle>
                </DialogHeader>
                <form onSubmit={generateReport} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="report-layers">Nº de camadas / folhas a cortar</Label>
                    <Input
                      id="report-layers"
                      data-testid="report-layers-input"
                      type="number"
                      min={1}
                      step={1}
                      required
                      autoFocus
                      value={reportLayers}
                      onChange={(e) => setReportLayers(e.target.value)}
                      placeholder="ex: 50"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      As quantidades da matriz de tamanhos e os metros de tecido são multiplicados por este valor.
                    </p>
                  </div>
                  <DialogFooter>
                    <button
                      type="submit"
                      disabled={generatingReport}
                      data-testid="report-generate-btn"
                      className="h-9 px-3 flex items-center gap-2 bg-primary text-primary-foreground text-sm font-medium rounded-[4px] hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                      {generatingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                      Gerar PDF
                    </button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
          {data && (
            <button
              data-testid="upload-new-btn"
              onClick={() => inputRef.current?.click()}
              className="h-9 px-3 flex items-center gap-2 bg-primary text-primary-foreground text-sm font-medium rounded-[4px] hover:opacity-90 transition-opacity"
            >
              <FileUp className="w-4 h-4" /> Carregar Novo
            </button>
          )}
          {user?.role === "super_admin" && (
            <button
              data-testid="admin-panel-link"
              onClick={() => navigate("/admin")}
              title="Painel de administração"
              className="w-9 h-9 border border-border flex items-center justify-center hover:bg-accent transition-colors"
            >
              <Building2 className="w-4 h-4" />
            </button>
          )}
          {user?.role === "company_admin" && (
            <button
              data-testid="company-admin-link"
              onClick={() => navigate("/empresa")}
              title="Gerir utilizadores da empresa"
              className="w-9 h-9 border border-border flex items-center justify-center hover:bg-accent transition-colors"
            >
              <Users className="w-4 h-4" />
            </button>
          )}
          <div className="hidden sm:flex items-center gap-1.5 pl-2 ml-1 border-l border-border">
            <div className="leading-none text-right">
              <div className="text-xs font-medium truncate max-w-[120px]">{user?.name}</div>
              <div className="text-[10px] text-muted-foreground truncate max-w-[120px]">{user?.company_name || "—"}</div>
            </div>
          </div>
          <button
            data-testid={LOGOUT.button}
            onClick={logout}
            title="Terminar sessão"
            className="w-9 h-9 border border-border flex items-center justify-center hover:bg-accent transition-colors"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>

      <input
        ref={inputRef}
        type="file"
        accept=".dxf,.gbr"
        className="hidden"
        data-testid="file-input"
        onChange={(e) => upload(e.target.files?.[0])}
      />

      {/* Body */}
      <div className="flex-1 min-h-0">
        {!data ? (
          <div className="h-full flex items-center justify-center p-6">
            <div
              data-testid="dropzone"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`w-full max-w-xl cursor-pointer border-2 border-dashed border-border bg-card p-12 text-center transition-colors ${dragOver ? "dropzone-active" : ""}`}
            >
              {loading ? (
                <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-primary" />
              ) : (
                <UploadCloud className="w-12 h-12 mx-auto mb-4 text-muted-foreground" strokeWidth={1.3} />
              )}
              <h2 className="font-head text-xl font-bold tracking-tight mb-2">
                {loading ? "A processar..." : "Carregar plano de corte"}
              </h2>
              <p className="text-sm text-muted-foreground mb-1 mono">
                Arraste o ficheiro <span className="text-foreground">.dxf</span> ou <span className="text-foreground">.gbr</span> ou clique para procurar
              </p>
              <p className="text-xs text-muted-foreground/70 mb-6">
                Lectra Modaris (DXF, AAMA/ASTM) ou plano de corte têxtil (GBR)
              </p>
              <div className="inline-flex items-center gap-2">
                <button
                  data-testid="load-sample-btn"
                  onClick={(e) => { e.stopPropagation(); loadSample(); }}
                  disabled={loading}
                  className="inline-flex items-center gap-2 text-xs mono px-3 h-8 border border-border hover:bg-accent transition-colors disabled:opacity-50"
                >
                  <Download className="w-3.5 h-3.5" /> Exemplo .dxf
                </button>
                <button
                  data-testid="load-sample-gbr-btn"
                  onClick={(e) => { e.stopPropagation(); loadSampleGbr(); }}
                  disabled={loading}
                  className="inline-flex items-center gap-2 text-xs mono px-3 h-8 border border-border hover:bg-accent transition-colors disabled:opacity-50"
                >
                  <Download className="w-3.5 h-3.5" /> Exemplo .gbr
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full grid grid-cols-12">
            {/* Sidebar */}
            <aside className="col-span-12 md:col-span-4 lg:col-span-3 border-r border-border flex flex-col min-h-0 bg-card">
              {/* file id */}
              <div className="p-3 border-b border-border flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Ficheiro</div>
                  <div data-testid="meta-id" className="mono text-sm truncate">{data.id}</div>
                </div>
                <button
                  data-testid="reset-btn"
                  onClick={() => setData(null)}
                  className="shrink-0 w-8 h-8 border border-border flex items-center justify-center hover:bg-accent transition-colors"
                  title="Limpar"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              </div>

              {/* metadata grid */}
              <div className="grid grid-cols-2 border-b border-border">
                <MetaCell icon={Ruler} label="Comprimento" value={fmtM(data.length_m)} testid="meta-length" />
                <MetaCell icon={Ruler} label="Largura" value={fmtM(data.width_m)} testid="meta-width" />
                <MetaCell icon={Percent} label="Eficiência" value={data.efficiency > 100 ? "—" : `${fmt(data.efficiency, 2)}%`} testid="meta-efficiency" accent={data.efficiency <= 100} />
                <MetaCell icon={Hash} label="Nº Peças" value={data.piece_count} testid="meta-count" />
              </div>

              {data.efficiency > 100 && (
                <div className="px-3 py-2 border-b border-border text-[10px] text-muted-foreground leading-snug">
                  Peças sobrepostas (ficheiro de gradação, não é um marker de corte). Eficiência não aplicável.
                </div>
              )}

              {/* selected piece dimensions */}
              <div
                data-testid="selected-piece-panel"
                className={`px-3 py-2.5 border-b border-border transition-colors ${selectedPiece ? "bg-[hsl(var(--accent-hl)/0.08)]" : ""}`}
              >
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">
                  <Maximize className="w-3 h-3" /> Peça selecionada
                </div>
                {selectedPiece && dims ? (
                  <div>
                    <div className="mono text-sm truncate" data-testid="selected-piece-name">
                      {selectedPiece.name}
                      {selectedPiece.size && selectedPiece.size !== "-" ? ` · ${selectedPiece.size}` : ""}
                      {selectedPiece.comment ? <span className="text-muted-foreground"> · {selectedPiece.comment}</span> : ""}
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      <div>
                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Largura</div>
                        <div className="mono text-sm" data-testid="selected-piece-width">{fmtCm(dims.w)}</div>
                      </div>
                      <div>
                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Comprimento</div>
                        <div className="mono text-sm" data-testid="selected-piece-height">{fmtCm(dims.h)}</div>
                      </div>
                      <div>
                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Área</div>
                        <div className="mono text-sm">{fmt(dims.area || 0, 3)} m²</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground">Passe o rato sobre uma peça para ver as medidas.</div>
                )}
              </div>

              {/* pieces list */}
              <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Lista de peças</span>
                <span className="text-[10px] mono text-muted-foreground">{data.units}</span>
              </div>
              <ScrollArea className="flex-1 min-h-0">
                <div className="grid grid-cols-[1fr_auto_auto_auto] text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border sticky top-0 bg-card">
                  <div className="px-3 py-2">Peça</div>
                  <div className="px-3 py-2 text-center">Tam.</div>
                  <div className="px-3 py-2 text-right">Qtd</div>
                  <div className="px-2 py-2"></div>
                </div>
                {data.piece_summary.map((pc) => {
                  const key = `${pc.name}||${pc.size || "-"}`;
                  const active = highlightKey === key;
                  const thisSize = data.pieces.filter((p) => p.name === pc.name && (p.size || "-") === (pc.size || "-"));
                  const sizeTag = pc.size && pc.size !== "-" ? pc.size : "";
                  return (
                    <div
                      key={key}
                      data-testid={`piece-row-${key}`}
                      onMouseEnter={() => setHighlightKey(key)}
                      onMouseLeave={() => setHighlightKey(null)}
                      className={`group grid grid-cols-[1fr_auto_auto_auto] items-center border-b border-border/60 cursor-pointer transition-colors ${active ? "bg-[hsl(var(--accent-hl)/0.14)]" : "hover:bg-accent"}`}
                    >
                      <div className="px-3 py-2 min-w-0">
                        <div className="mono text-xs truncate">{pc.name}</div>
                        {pc.comment && (
                          <div className="text-[10px] text-muted-foreground truncate uppercase tracking-wide">{pc.comment}</div>
                        )}
                      </div>
                      <div className="px-3 py-2 text-center">
                        <span className="mono text-[11px] px-1.5 py-0.5 border border-border">{pc.size && pc.size !== "-" ? pc.size : "—"}</span>
                      </div>
                      <div className="px-3 py-2 text-right mono text-xs text-muted-foreground">{pc.qty}</div>
                      <div className="px-2 py-2">
                        <button
                          data-testid={`export-piece-${key}`}
                          onClick={(e) => { e.stopPropagation(); exportDxf(thisSize, `${data.id}_${pc.name}${sizeTag ? "_" + sizeTag : ""}`); }}
                          disabled={exporting}
                          className="w-7 h-7 flex items-center justify-center border border-transparent hover:border-border hover:bg-card text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40"
                          title={`Exportar ${pc.name}${sizeTag ? " · " + sizeTag : ""} — só linha exterior, DXF (mm)`}
                        >
                          <FileDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </ScrollArea>
            </aside>

            {/* Canvas */}
            <main className="col-span-12 md:col-span-8 lg:col-span-9 min-h-0 relative">
              <DxfCanvas
                data={data}
                highlightKey={highlightKey}
                setHighlightKey={setHighlightKey}
                showLabels={showLabels}
              />
              {loading && (
                <div className="absolute inset-0 z-30 bg-background/60 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              )}
            </main>
          </div>
        )}
      </div>
    </div>
  );
}
